import * as React from "react";
import { Strings } from "--client/../strings/en";
import { SwaggerMutateClient } from "--client/common/apis/rest";
import { Body1, Heading5 } from "--components/typography"
import { useToastNotifications } from '--floponents-web/molecules/ToastNotification/ToastProvider';
import { TextInput } from "--floponents-web/atoms/TextInput";
import { Form } from "--floponents-web/atoms/Form";
import { Button } from "--floponents-web/atoms/Button";
import { useSystemDialogContext } from "../../components/SystemDialog/SystemDialog";
import { useHistory } from "react-router-dom";
import { SitemapRouteProps } from "../../../sitemap";
import { UserSettingsSitemapRouting } from "--client/../static_types/sitemaps/userSettings";
import { UserPreferencesContentLayout } from "./UserPreferencesContentLayout";
import { AuthSitemapRouting } from "--client/../static_types/sitemaps/auth";
import { ArgsChangeUser } from "--client/../static_types/api_methods/mutationMethods.mutation.current";
import { CurrentAuthToken } from "--client/common/TypedStore";
import { producerSitemap } from "--client/../sitemaps/sitemaps";
import { AuthBackground } from "../../../common/components/Auth/AuthRouter";
import { Surface, SurfaceHeader } from "--floponents-web/atoms/Surface";
import styled from "styled-components";
const STRINGS = Strings.authentication;

type UserSettingsProps = SitemapRouteProps<
    Pick<
        UserSettingsSitemapRouting,
        "/userPrefs/:user_id/changePassword"
    >
>
    |
    SitemapRouteProps<
        Pick<AuthSitemapRouting, "/auth/changePassword/:token">
    >
const StyledChangePasswordSurface = styled(Surface)`
    min-width: calc(min(400px,90vw));
`
const StyledChangePassword = styled.div`
    padding:${p => p.theme.spacing(2, 0)};
`
const ChangedPasswordLayout: React.FC<UserSettingsProps> = props => {
    if (props.$$routeProps.$$routeKey === "/userPrefs/:user_id/changePassword")
        return <UserPreferencesContentLayout
            user_id={props.$$routeProps.$$routeKey === "/userPrefs/:user_id/changePassword" ? props.$$routeProps.user_id : null}
            title={<Strings.Inline>Change your password</Strings.Inline>}
        >
            <StyledChangePassword>
                {props.children}
            </StyledChangePassword>
        </UserPreferencesContentLayout>
    else
        return <AuthBackground>

            <StyledChangePasswordSurface elevation={1}>
                <SurfaceHeader>
                    <Heading5>Change password</Heading5>
                </SurfaceHeader>
                {props.children}
            </StyledChangePasswordSurface>
        </AuthBackground>
}

/**
 * 
 * @returns Component for changing user password
 */
export const ChangePassword: React.FC<UserSettingsProps> = props => {
    const history = useHistory();
    const [confirmPassword, setConfirmPassword] = React.useState<string>("");
    const [newPassword, setNewPassword] = React.useState<string>("");
    const [, setSystemDialog] = useSystemDialogContext();
    const [error, setError] = React.useState<Error>();
    const toast = useToastNotifications();

    React.useEffect(() => {
        if (error)
            toast.addToast(<Body1>{error.message || "Unknown error"}</Body1>, { appearance: "error" })
    }, [error])


    const changeUser = async (
        auth: ArgsChangeUser["auth"]
    ) => {
        const result = await SwaggerMutateClient.ChangeUser({
            auth,
            changes: {
                newPassword: newPassword
            }
        });
        if (result) {
            await CurrentAuthToken.set({
                ...result,
                user_or_identity_id: result.user_id
            })
            history.push(producerSitemap.$$createUrl({}))
        }
        toast.addToast(Strings.inline("Password changed"), { appearance: "success" });
    }
    const submitForm = React.useCallback(async () => {
        try {

            if (newPassword === "") return
            if (newPassword !== confirmPassword) throw new Error("Passwords do not match");
            if (props.$$routeProps.$$routeKey === "/auth/changePassword/:token")
                return changeUser({
                    type: "token",
                    token: props.$$routeProps.token
                });
            else
                setSystemDialog({
                    type: "reSignIn",
                    onCredentials: async (username, password) => {
                        if (!username || !password)
                            throw new Error("No username or password")
                        return changeUser({
                            type: "usernamepassword",
                            password,
                            username
                        })
                    }
                });
        } catch (e) {
            setError(e);
        }
    }, [newPassword, confirmPassword]);

    // TODO: update login details gets overridden by the login details for MiniLogin dialog
    return <ChangedPasswordLayout {...props}>
        <form onSubmit={submitForm} action="javascript:void(0);">
            <Form.FormSection>
                <Form.FormFields>
                    <TextInput
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e, val) => setNewPassword(val)}
                        label={Strings.UserSettings.password.newPassword}
                        placeholder={STRINGS.newPasswordField} />
                    <TextInput
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e, val) => setConfirmPassword(val)}
                        label={Strings.UserSettings.password.confirmPassword}
                        placeholder={STRINGS.confirmPasswordField} />
                </Form.FormFields>
            </Form.FormSection>
            <Form.ActionsGutter>
                <Button
                    type="submit"
                    color={"positive"}
                    disabled={!newPassword || !confirmPassword}>
                    {Strings.save}
                </Button>
                <Button
                    type="button"
                    color={"grey"}
                    onClick={() => history.go(-1)}>
                    {Strings.cancel}
                </Button>
            </Form.ActionsGutter>
        </form>
    </ChangedPasswordLayout>
}
